
// Tipos para la API de Median (JavaScript Bridge)
interface MedianOneSignalInfo {
    userId: string; // OneSignal Player ID
    pushToken: string;
    subscribed: boolean;
}

interface MedianInterface {
    onesignal?: {
        info?: () => Promise<MedianOneSignalInfo>;
        register?: () => void;
    };
    // Otros métodos de Median pueden ir aqui
}

// Extender el objeto window
declare global {
    interface Window {
        median?: MedianInterface;
        // Compatibilidad con versiones antiguas
        gonative?: any;
    }
}

/**
 * Detecta si la app se está ejecutando dentro de Median
 */
export const isMedianApp = (): boolean => {
    return navigator.userAgent.includes('Median') ||
        navigator.userAgent.includes('gonative') ||
        navigator.userAgent.includes('SkyMenuApp') ||
        !!window.median ||
        !!window.gonative;
};

/**
 * Obtiene la información de OneSignal desde la App Nativa
 */
// Función auxiliar para esperar
const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Obtiene la información de OneSignal desde la App Nativa con reintentos
 */
export const getOneSignalInfo = async (retries = 3): Promise<MedianOneSignalInfo | null> => {
    // 1. Detección básica inicial
    if (!isMedianApp()) {
        console.warn('No es entorno Median/App.');
        return null;
    }

    // Función interna para intentar obtener la info una vez
    const attemptFetch = (): Promise<MedianOneSignalInfo | null> => {
        return new Promise((resolve) => {
            // Check Median Interface
            if (window.median?.onesignal?.info) {
                window.median.onesignal.info()
                    .then((info) => {
                        // A veces devuelve { success: true, oneSignalUserId: '...' } o directo el objeto
                        const userId = (info as any).oneSignalUserId || info.userId;
                        const pushToken = (info as any).pushToken || info.pushToken;

                        if (userId) {
                            resolve({ userId, pushToken, subscribed: true });
                        } else {
                            resolve(null);
                        }
                    })
                    .catch(e => {
                        console.error('Error llamada Median:', e);
                        resolve(null);
                    });
                return;
            }

            // Check GoNative Interface (Legacy)
            if (window.gonative?.onesignal?.info) {
                window.gonative.onesignal.info().then((info: any) => {
                    const userId = info.oneSignalUserId || info.userId;
                    if (userId) resolve({ userId, pushToken: info.pushToken, subscribed: true });
                    else resolve(null);
                }).catch(() => resolve(null));
                return;
            }

            resolve(null);
        });
    };

    // 2. Bucle de intentos (Polling inteligente)
    // Intentaremos buscar la API durante 15 segundos (a veces tarda en cargar)
    const startTime = Date.now();

    while (Date.now() - startTime < 15000) {
        // A. Intentar con objeto 'median' (Moderno)
        if (window.median?.onesignal) {
            // Forzar registro por si acaso no se ha inicializado
            if (window.median.onesignal.register) window.median.onesignal.register();

            if (window.median.onesignal.info) {
                const result = await attemptFetch();
                if (result && result.userId) return result;
            }
        }

        // B. Intentar con objeto 'gonative' (Legacy)
        if (window.gonative?.onesignal) {
            if (window.gonative.onesignal.register) window.gonative.onesignal.register();

            if (window.gonative.onesignal.info) {
                const result = await attemptFetch();
                if (result && result.userId) return result;
            }
        }

        // Esperar un poco antes de volver a mirar
        await wait(1000);
    }

    console.warn('Timeout esperando OneSignal Info en Median App (15s)');
    return null;
};
